import { useMemo, useState } from "react";
import {
  useParams,
  useRouteContext,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";
import { useLeaData } from "@/queries.ts";
import { STFF, STAFF_Key } from "@/models/stff";
import LayoutContentArea from "@/components/layout/LayoutContentArea";
import Card from "@/components/shared/Card";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import {
  StaffSearchForm,
  StaffSearchFormState,
} from "@/components/records/staff/StaffSearchForm";
import { AddStaffWizard } from "@/components/records/staff/add-staff-wizard/AddStaffWizard";
import { CSVDownloadButton } from "@/components/shared/CSVDownloadButton";
import { getCurrentProgramYear } from "@/utils/dates";

type StaffSearchQuery = {
  programYear?: string;
  localStaffId?: string;
};

type StaffRow = STFF & {
  FirstName: string;
  LastName: string;
};

const staffCsvHeaders = [
  { label: "SEID", key: "SEID" },
  { label: "Staff Local ID", key: "LocalStaffId" },
  { label: "First Name", key: "FirstName" },
  { label: "Last Name", key: "LastName" },
];

export default function StaffTablePage() {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const search = useSearch({ strict: false }) as StaffSearchQuery;
  const leaId = useParams({ from: "/$leaid" })?.leaid;
  const { data: lea, error, isLoading } = useLeaData(leaId);
  const navigate = useNavigate({ from: "/$leaid" });

  const [showWizard, setShowWizard] = useState(false);

  const staffRecords: STFF[] = useMemo(
    () => Object.values(lea?.entities.staff ?? {}).map((x) => x.records[0]),
    [lea?.entities.staff]
  );

  const columns: GridColumnProps[] = useMemo(
    () => [
      {
        id: "SEID",
        field: "SEID",
        title: "SEID",
        filterable: false,
      },
      {
        id: "LocalStaffId",
        field: "LocalStaffId",
        title: "Staff Local ID",
        filterable: false,
      },
      {
        id: "FirstName",
        field: "FirstName",
        title: "First Name",
        filterable: false,
      },
      {
        id: "LastName",
        field: "LastName",
        title: "Last Name",
        filterable: false,
      },
    ],
    []
  );

  const tableData: StaffRow[] = useMemo(
    () =>
      staffRecords.map((record) => ({
        ...record,
        FirstName: record.StaffLegalFirstName,
        LastName: record.StaffLegalLastName,
        SEID: record.SEID?.trim() ? record.SEID : "-",
      })),
    [staffRecords]
  );

  const programYearOptions = useMemo(
    () =>
      Array.from(
        new Set(staffRecords.map((s) => s.ProgramYear).filter(Boolean))
      ),
    [staffRecords]
  );

  const [appliedFilters, setAppliedFilters] = useState<StaffSearchFormState>(
    () => ({
      programYear: search.programYear ?? getCurrentProgramYear(),
      seid: "",
      firstName: "",
      middleName: "",
      lastName: "",
      localStaffId: search.localStaffId ?? "",
    })
  );

  const isInitialLoadDone = !isLoading;

  const handleSearch = (filters: StaffSearchFormState) => {
    if (!isInitialLoadDone) return;
    setAppliedFilters(filters);
  };

  const handleClearFilters = () => {
    navigate({
      to: "/$leaid/records/staff",
      params: { leaid: leaId },
    });
  };

  const filteredData: StaffRow[] = useMemo(() => {
    if (!isInitialLoadDone) return tableData;

    const seid = appliedFilters.seid.trim().toLowerCase();
    const first = appliedFilters.firstName.trim().toLowerCase();
    const middle = appliedFilters.middleName.trim().toLowerCase();
    const last = appliedFilters.lastName.trim().toLowerCase();
    const localId = appliedFilters.localStaffId.trim().toLowerCase();
    const year = appliedFilters.programYear.trim();

    return tableData.filter((row) => {
      if (year && row.ProgramYear !== year) return false;

      if (seid && !row.SEID?.toLowerCase().includes(seid)) {
        return false;
      }

      if (first && !row.FirstName?.toLowerCase().includes(first)) {
        return false;
      }

      if (middle && !row.StaffLegalMiddleName?.toLowerCase().includes(middle)) {
        return false;
      }

      if (last && !row.LastName?.toLowerCase().includes(last)) {
        return false;
      }

      if (
        localId &&
        !row.LocalStaffId?.toString().toLowerCase().includes(localId)
      ) {
        return false;
      }

      return true;
    });
  }, [tableData, appliedFilters, isInitialLoadDone]);

  return (
    <LayoutContentArea
      headingText="Staff Records"
      headerActions={
        <HeaderActions
          onAddButtonClick={() => setShowWizard(true)}
          data={filteredData}
        />
      }
    >
      <AddStaffWizard
        show={showWizard}
        onClose={() => setShowWizard(false)}
        preschools={leaContext.currentOrgPreschools}
      />

      <Card className="card-custom mb-5 shadow-none">
        <StaffSearchForm
          programYearOptions={programYearOptions}
          disabled={!isInitialLoadDone}
          onSearch={handleSearch}
          initialProgramYear={search.programYear}
          initialLocalStaffId={search.localStaffId}
          onClear={handleClearFilters}
        />
      </Card>

      <Card className="table-style mb-3">
        <KendoTableClientState
          key={`${appliedFilters.programYear}-${appliedFilters.localStaffId}-${appliedFilters.seid}-${appliedFilters.firstName}-${appliedFilters.middleName}-${appliedFilters.lastName}`}
          data={filteredData}
          columns={columns}
          error={error}
          filterable={false}
          onRowClick={(event: GridRowClickEvent) => {
            const staff = event.dataItem as StaffRow;
            const staffId = STAFF_Key(staff);
            navigate({
              to: "/$leaid/records/staff/$staffId",
              params: { leaid: leaId, staffId },
            }).catch((navError) => {
              console.error("Navigation error:", navError);
            });
          }}
        />
      </Card>
    </LayoutContentArea>
  );
}

function HeaderActions({
  onAddButtonClick,
  data,
}: {
  onAddButtonClick: () => void;
  data: StaffRow[];
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });

  return (
    <div className="d-flex align-items-center">
      <CSVDownloadButton
        data={data}
        headers={staffCsvHeaders}
        filename="staff-records.csv"
      />
      {leaContext.hasRole("STFFEdit") && (
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={onAddButtonClick}
        >
          <i className="bi bi-person-plus"></i>
          Add Staff
        </button>
      )}
    </div>
  );
}
